import { isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { UserQuery } from '@core/store/user/user.query';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideMenu } from '@ng-icons/lucide';
import { V2_ICONS } from '@v2/v2.icons';
import { V2ThemeService } from '@v2/shared/v2-theme.service';
import { V2BrandComponent } from '../brand/v2-brand.component';
import { V2NotificationBellComponent } from '../notification-bell/v2-notification-bell.component';

@Component({
  selector: 'app-v2-top-bar',
  templateUrl: './v2-top-bar.component.html',
  styleUrls: ['./v2-top-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [NgIcon, V2BrandComponent, V2NotificationBellComponent],
  // `lucideMenu` is registered here rather than in `V2_ICONS`: the rail toggle
  // is the only thing in the app that needs it, and `v2.icons.ts` is shared
  // ground that other work is on right now.
  providers: [provideIcons({ ...V2_ICONS, lucideMenu })],
})
export class V2TopBarComponent implements OnInit, OnDestroy {
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly userQuery = inject(UserQuery);
  protected readonly theme = inject(V2ThemeService);

  /**
   * Gates the notification bell below. `null` until the user request settles,
   * which reads the same as signed-out -- the bell polls an authenticated
   * endpoint, so mounting it before a session is confirmed would 401 the same
   * as mounting it for a visitor who never signs in.
   */
  protected readonly user = toSignal(this.userQuery.getUser$, { initialValue: null });

  /** Drives which face the rail toggle wears — `menu` to open, `x` to close. */
  readonly isSidebarHidden = input<boolean>(false);

  readonly sidebarToggle = output<void>();
  readonly accountToggle = output<void>();

  /** Zulu, because that is the time a flight plan is written in. */
  protected readonly utc = signal(this.nowUtc());
  private timer?: ReturnType<typeof setInterval>;

  ngOnInit(): void {
    // The server renders the clock once and never ticks it: an interval there
    // would keep the render alive and never be cleared.
    if (!this.isBrowser) {
      return;
    }
    this.timer = setInterval(() => this.utc.set(this.nowUtc()), 30_000);
  }

  ngOnDestroy(): void {
    if (this.timer) {
      clearInterval(this.timer);
    }
  }

  /** The label says what pressing it will do, not what is currently showing. */
  protected themeLabel(): string {
    return this.theme.theme() === 'dark' ? 'Switch to light theme' : 'Switch to dark theme';
  }

  private nowUtc(): string {
    const now = new Date();
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${pad(now.getUTCHours())}:${pad(now.getUTCMinutes())} UTC`;
  }
}
